/**
 * Builds the serializable model for the organization (Structure / Supply Chain) HTML report.
 * The model holds plain text and numbers only: no HTML, internal IDs, tokens or API URLs.
 */
import { brsrCompleteness, brsrDataGaps, buildBrsrPrinciple6 } from "../../functions/results/brsrIndicators";
import { KG_PER_TONNE, toFiniteNumber } from "../../functions/results/formatters";
import { normalizeEmissionDetails, normalizeEmissions } from "../../functions/results/organizationResults";
import { REPORT_SCHEMA_VERSION, REPORT_TYPES, plainText } from "./reportModel";
import { HTML_REPORT_FEATURES } from "../reportConfig";

const isFlagSet = (value) => value === true || value === 1 || value === "1";

// ghg_assessment fields shown in the methodology section, in display order.
const METHODOLOGY_FIELDS = [
  ["boundary", "Organizational boundary"],
  ["base_year", "Base year"],
  ["context", "Context"],
  ["primary_information", "Primary information"],
  ["exclusion", "Exclusions"],
  ["assumption", "Assumptions"],
  ["clarification", "Clarifications"],
  ["biogenic_emission", "Biogenic emissions"],
  ["functional_unit", "Functional unit"],
];

const SHORT_TERM_TARGET_FIELDS = [
  ["base_year", "Base year"],
  ["target_year", "Target year"],
  ["most_recent_year", "Most recent year"],
  ["target_setting_method", "Target setting method"],
];

const LONG_TERM_TARGET_FIELDS = [
  ["base_year", "Base year"],
  ["target_year", "Target year"],
  ["target_coverage", "Target coverage"],
  ["sector_pathway", "Sector pathway"],
];

// net_zero_tool.type values and their display labels.
const HORIZON_LABELS = { "short-term": "Short-term", "long-term": "Long-term" };

const pickFields = (record, fields, translate) =>
  record
    ? fields
        .map(([key, label]) => ({ key, label: translate(label), value: plainText(record[key]) }))
        .filter((item) => item.value !== "")
    : [];

const firstRecord = (value) => (Array.isArray(value) ? value[0] : value) || null;

const translateBrsrGroup = (group, translate) =>
  group.map((indicator) => ({
    ...indicator,
    title: translate(indicator.title),
    rows: indicator.rows.map((row) => ({ ...row, label: translate(row.label), source: translate(row.source) })),
  }));

/** BRSR-only content: Scope 3 coverage, targets, reduction actions and Principle 6 indicators. */
const buildBrsrContent = ({ emissionRows, detailRows, ghgAssessment, shortTermTargets, longTermTargets, reductionActions, translate }) => {
  // Rows saved from an empty grid line carry no action, category or details.
  const actions = (reductionActions || []).filter((a) => a && (plainText(a.action) || plainText(a.category) || plainText(a.details)));
  const brsr = buildBrsrPrinciple6({ emissionRows, detailRows, reductionActions: actions });

  return {
    scope3Included: ghgAssessment ? isFlagSet(ghgAssessment.is_scope_3_included) : null,
    targets: {
      shortTerm: pickFields(firstRecord(shortTermTargets), SHORT_TERM_TARGET_FIELDS, translate),
      longTerm: pickFields(firstRecord(longTermTargets), LONG_TERM_TARGET_FIELDS, translate),
    },
    reductionActions: actions.map((a) => ({
      horizon: translate(HORIZON_LABELS[a.type] || plainText(a.type)),
      action: plainText(a.action),
      category: plainText(a.category),
      year: a.year ? String(a.year) : "",
      details: plainText(a.details),
      // estimated_ghg is captured in t CO₂e in the net zero tools
      estimatedReductionKg: a.estimated_ghg === null || a.estimated_ghg === undefined || a.estimated_ghg === "" ? null : toFiniteNumber(a.estimated_ghg) * KG_PER_TONNE,
    })),
    brsr: {
      essential: translateBrsrGroup(brsr.essential, translate),
      leadership: translateBrsrGroup(brsr.leadership, translate),
    },
    completeness: brsrCompleteness(brsr),
    dataGaps: brsrDataGaps(brsr).map((gap) => ({ ...gap, title: translate(gap.title), missing: gap.missing.map(translate) })),
  };
};

/**
 * @param {object} input
 * @param {{ name: string, year?: number }} input.entity - the Structure or Supply Chain record
 * @param {"Organization"|"Supply chain"} input.entityKind
 * @param {Array} input.emissions
 * @param {Array} input.emissionDetails
 * @param {object} input.emissionDetailOptions - from ContextProvider, used for pollutant factors
 * @param {object} input.ghgAssessment
 * @param {Array|object} input.shortTermTargets - used only with features.brsr
 * @param {Array|object} input.longTermTargets - used only with features.brsr
 * @param {Array} input.reductionActions - net_zero_tool rows; used only with features.brsr
 * @param {{ brsr: boolean }} input.features - see HTML_REPORT_FEATURES
 * @param {function} input.translate - i18n t function
 * @param {string} input.language
 * @param {Date} input.generatedAt
 */
export const buildOrganizationReport = ({
  entity,
  entityKind,
  emissions = [],
  emissionDetails = [],
  emissionDetailOptions,
  ghgAssessment,
  shortTermTargets,
  longTermTargets,
  reductionActions = [],
  features = HTML_REPORT_FEATURES,
  translate = (s) => s,
  language = "en",
  generatedAt = new Date(),
}) => {
  const emissionRows = normalizeEmissions(emissions, emissionDetails);
  const detailRows = normalizeEmissionDetails(emissionDetails, emissionRows, emissionDetailOptions);
  const includeBrsr = Boolean(features?.brsr);

  return {
    schemaVersion: REPORT_SCHEMA_VERSION,
    reportType: REPORT_TYPES.organization,
    features: { brsr: includeBrsr },
    meta: {
      entityName: plainText(entity?.name) || translate("Unnamed entity"),
      entityKind: translate(entityKind),
      reportingYear: entity?.year ? String(entity.year) : "",
      generatedAt: generatedAt.toISOString(),
      language,
    },
    emissions: emissionRows.map(({ scope, category, description, gwpKg }, rowId) => ({ rowId, scope, category, description: plainText(description), gwpKg })),
    details: detailRows.map(({ scope, category, description, quantity, unit, calculationMethod, co2eKg, pollutantsKg }, rowId) => ({
      rowId,
      scope,
      category,
      description: plainText(description),
      quantity,
      unit: plainText(unit),
      calculationMethod: plainText(calculationMethod),
      co2eKg,
      pollutantsKg,
    })),
    methodology: pickFields(ghgAssessment, METHODOLOGY_FIELDS, translate),
    ...(includeBrsr
      ? buildBrsrContent({ emissionRows, detailRows, ghgAssessment, shortTermTargets, longTermTargets, reductionActions, translate })
      : {}),
  };
};
